import { updateProfile } from 'firebase/auth';
import { collection } from 'firebase/firestore';
import { getDownloadURL, ref, uploadString } from 'firebase/storage';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';

import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';
import { useForm } from '../../../../hooks';
import { checkFieldExist, getBase64, getUser, updateUser } from '../../../../services';
import { auth, firestore, storage } from '../../../../services/Firebase';
import { Splash } from '../../../../shared/Components';
import { FlexContainer, Text } from '../../../../shared/Typhography';
import { strings } from '../../../../strings';
import { AccountInfoField, accountInfoSchema } from '../../../../validations/accountInfo';

import { SettingsOption } from './Components';

export interface AccountInfoInputs {
  [AccountInfoField.NAME]: string;
  [AccountInfoField.USERNAME]: string;
}
interface Option {
  type?: 'image' | 'password';
  name: any;
  value?: any;
  image?: any;
  handleEdit?: () => void;
}

const AccountInfoScreen = () => {
  const user = auth.currentUser || ({} as any);
  const { data: userData, isLoading } = getUser(user.uid);
  const [loading, setLoading] = React.useState<boolean>(false);
  const { mutate: update } = updateUser(user.uid);
  const [profileImage, setProfileImage] = React.useState<any>(undefined);
  const { showModal } = useGlobalModalContext();
  const {
    control,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<AccountInfoInputs>({
    schema: accountInfoSchema,
  });

  const getImage = async (newImageFile: any[]) => {
    const mappedFiles = await Promise.all(
      newImageFile.map(async (item) => {
        return { ...item, url: await getBase64(item) };
      }),
    );
    const url = mappedFiles[0].url;
    setProfileImage(url);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/jpeg,image/png',
    onDrop: getImage,
  });

  const openUpdatePassword = () => {
    showModal(MODAL_TYPES.UPDATE_PASSWORD_MODAL, {
      title: strings.changePassword,
    });
  };

  /**
   * Function for changing username
   */
  const changeUsername = handleSubmit(async (data) => {
    const refUsers = collection(firestore, 'usersWeb');
    const exist = await checkFieldExist(refUsers, 'username', data.Username);
    if (exist) {
      toast.error(strings.usernameExist);
      reset();
      return;
    } else {
      update({
        username: data.Username,
      });
      toast.success(strings.successfullyUpdated);
    }
  });

  /**
   * Function for change name
   */
  const changeName = handleSubmit((data) => {
    update({
      Name: data.Name,
    });
    toast.success(strings.successfullyUpdated);
  });

  /**
   * Function for submiting changing profile image.
   */
  const submitUpdateProfileImage = async () => {
    setLoading(true);
    try {
      const profileImageRef = ref(storage, `profilephotos/${user.uid}`);
      await uploadString(profileImageRef, profileImage, 'data_url');
      const url = await getDownloadURL(profileImageRef);
      update({
        profileImageUrl: url,
      });
      updateProfile(user, {
        photoURL: url,
      });
      setLoading(false);
      toast.success(strings.successfullyUpdated);
      setProfileImage(undefined);
    } catch (error) {
      toast.error(strings.toastNotificationSomethingWrong);
      setLoading(false);
    }
  };

  /**
   * I made array of all options for better reading and not repeating myself.
   */
  const options: Option[] = [
    { name: AccountInfoField.NAME, value: userData?.data()?.Name, handleEdit: changeName },
    {
      name: AccountInfoField.PROFILE_IMAGE,
      value: profileImage ? profileImage : userData?.data()?.profileImageUrl,
      handleEdit: () => submitUpdateProfileImage(),
      image: profileImage,
      type: 'image',
    },
    {
      name: AccountInfoField.USERNAME,
      value: userData?.data()?.username,
      handleEdit: changeUsername,
    },
    {
      name: AccountInfoField.PASSWORD,
      value: '*********',
      handleEdit: openUpdatePassword,
      type: 'password',
    },
  ];

  if (isLoading) {
    return <Splash />;
  }

  return (
    <FlexContainer gap="1rem" width="75%" flexDirection="column">
      <Text weight="bold" size={2.4}>
        {strings.accountInfo}
      </Text>
      {options.map((option, index) => (
        <SettingsOption
          key={index}
          name={option.name}
          type={option.type}
          value={option.value}
          image={option.image}
          setImage={setProfileImage}
          handleEdit={option.handleEdit}
          getInputProps={getInputProps()}
          getRootProps={getRootProps()}
          getValues={getValues}
          control={control}
          errors={errors}
          loading={loading}
        />
      ))}
    </FlexContainer>
  );
};

export default AccountInfoScreen;
